// File: src/pages/AddOtherChargesList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Form,
  FormGroup,
  Label,
} from "reactstrap";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";

import { FaPlus, FaFilter, FaPencilAlt } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { Trash2 } from "react-feather";

// ---- API layer & utils (your project paths) ----
import {
  _Get,
  _PostSave,
  _Put,
  _Delete,
} from "../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import {
  AddOther_Charges,
  DeleteOne_Other_Charges,
  UpdateOne_Other_Charges,
  ViewOther_Charges,
} from "../import/ApiEndPoint/Api";

// ---- tiny CSS injected ONCE (page scoped) ----
const injectOnce = () => {
  if (document.getElementById("othercharges-inline-css")) return;
  const style = document.createElement("style");
  style.id = "othercharges-inline-css";
  style.innerHTML = `
    .oc-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .oc-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .oc-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:760px; }
    .oc-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .oc-table tbody td{
      padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .oc-table tbody tr:hover{ background:#f9fbff; }

    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer }
    .chip-edit{ background:#d46f10; }
    .chip-delete{ background:#ec1809; }

    .oc-form .form-group { margin-bottom:8px; }
    .oc-form label { font-size:12px; margin-bottom:4px; }
    .oc-form input, .oc-form select { font-size:12px; height:34px; }
  `;
  document.head.appendChild(style);
};

// ---- helpers ----
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const fmtDate = (s) => (s ? String(s).split("T")[0] : "");
const toNumber = (v) => (isNaN(Number(v)) ? 0 : Number(v));

export default function AddOtherChargesList() {
  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [search, setSearch] = useState("");

  // Column chooser state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // Add/Edit modal
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    type: "", // "Charges" | "Discount"
    discounttype: "", // "Amount" | "Percentage"
    title: "",
    amount: "",
    percentage: "",
  });

  useEffect(() => injectOnce(), []);

  // ----- Define columns -----
  const baseColumns = useMemo(
    () => [
      { key: "title", header: "TITLE", get: (r) => r?.title },
      {
        key: "type",
        header: "NARRATION TYPE",
        get: (r) => r?.type,
        render: (r) => (
          <span
            style={{
              fontWeight: 700,
              color: r?.type === "Charges" ? "#7c2d12" : "#065f46",
            }}
          >
            {r?.type || "-"}
          </span>
        ),
      },
      { key: "discounttype", header: "MODE", get: (r) => r?.discounttype },
      {
        key: "amount",
        header: "AMOUNT (₹)",
        get: (r) => (r?.amount === 0 || r?.amount ? `₹${r.amount}` : "---"),
      },
      {
        key: "percentage",
        header: "PERCENTAGE (%)",
        get: (r) =>
          r?.percentage === 0 || r?.percentage ? `${r.percentage}%` : "---",
      },
      {
        key: "createdAt",
        header: "CREATED",
        get: (r) => fmtDate(r?.createdAt),
      },
      {
        key: "updatedAt",
        header: "UPDATED",
        get: (r) => fmtDate(r?.updatedAt),
      },
    ],
    []
  );

  const actionsColumn = useMemo(
    () => ({
      key: "__actions__",
      header: "ACTIONS",
      get: () => "",
      render: (r) => (
        <div className="action-chips">
          {permissions?.Edit && (
            <span
              className="chip chip-edit"
              title="Edit"
              onClick={() => openForm("Update", r)}
            >
              <FaPencilAlt size={14} color="white" />
            </span>
          )}
          {permissions?.Delete && (
            <span
              className="chip chip-delete"
              title="Delete"
              onClick={() => handleDelete(r?._id)}
            >
              <Trash2 size={14} color="white" />
            </span>
          )}
        </div>
      ),
    }),
    [permissions]
  );

  // Column map
  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    map.set(actionsColumn.key, actionsColumn);
    return map;
  }, [baseColumns, actionsColumn]);

  // Restore or default columns
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("OtherChargesCols");
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        nextKeys = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setSelectedKeys(nextKeys);
  }, [baseColumns, colMap]);

  // ----- Fetch data -----
  const reload = async () => {
    const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
    const uid = userinfo?._id || userinfo?.id || "";
    const db = userinfo?.database || userinfo?.db || "";
    if (!uid || !db) throw new Error("Missing _id or database in userData");

    // permissions
    const inside = CheckPermission("Add Charges");
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    // fetch
    const res = await _Get(ViewOther_Charges, db);
    const list = Array.isArray(res?.OtherCharges) ? res.OtherCharges : [];

    // Master/SuperAdmin see all; others only own records (if applicable to your data)
    let filtered = list;
    const roleName = userinfo?.rolename?.roleName;
    if (!(roleName === "MASTER" || roleName === "SuperAdmin")) {
      // keep all—no strict owner field provided in sample
      filtered = list;
    }

    setRows(filtered);
    setAllRows(filtered);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[AddOtherChargesList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load charges/discounts. Check console for details."
        );
        setRows([]);
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ----- Visible columns & search -----
  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn]; // include Actions at end
  }, [selectedKeys, colMap, actionsColumn]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const searchCols = visibleCols.filter((c) => c.key !== "__actions__");
    return rows.filter((r) =>
      searchCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term))
    );
  }, [rows, search, visibleCols]);

  // ----- Actions -----
  const handleDelete = (id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        _Delete(DeleteOne_Other_Charges, id)
          .then(() => {
            setRows((prev) => prev.filter((x) => x._id !== id));
            setAllRows((prev) => prev.filter((x) => x._id !== id));
          })
          .catch(() => {});
      }
    });
  };

  const handleExportPDF = async () => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r)).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "OtherCharges");
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`
    );
  };

  // ----- Column Chooser Modal logic -----
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current])
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("OtherChargesCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  // ----- Add/Edit form -----
  const resetForm = () =>
    setFormData({
      type: "",
      discounttype: "",
      title: "",
      amount: "",
      percentage: "",
    });

  const openForm = (mode, row = null) => {
    if (mode === "Create") {
      setEditId(null);
      resetForm();
      setShowForm(true);
      return;
    }
    // Update mode
    setEditId(row?._id || null);
    setFormData({
      type: row?.type || "",
      discounttype: row?.discounttype || "",
      title: row?.title || "",
      amount: row?.amount ?? "",
      percentage: row?.percentage ?? "",
    });
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const payload = {
        type: formData?.type,
        discounttype: formData?.discounttype,
        title: formData?.title,
        database: user?.database,
      };
      if (formData?.discounttype === "Amount") {
        payload.amount = toNumber(formData?.amount);
        payload.percentage = undefined;
      } else if (formData?.discounttype === "Percentage") {
        payload.percentage = toNumber(formData?.percentage);
        payload.amount = undefined;
      }

      if (editId) {
        await _Put(UpdateOne_Other_Charges, editId, payload);
        swal("Updated successfully");
      } else {
        await _PostSave(AddOther_Charges, payload);
        swal("Added successfully");
      }

      await reload();
      setShowForm(false);
      setEditId(null);
      resetForm();
    } catch (err) {
      console.log(err);
      swal("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // ----- Loading -----
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {/* Error banner if fetch failed */}
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="oc-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Discount / Charges{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() => openForm("Create")}
                >
                  <FaPlus size={10} /> Charges / Discount
                </Button>
              )}

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() => handleExportXLSX("OtherCharges")}
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No data found.
              </div>
            ) : (
              <div className="oc-table-wrap">
                <table className="oc-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column Chooser Modal */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showForm}
        toggle={() => setShowForm(false)}
        style={{ maxWidth: 700 }}
      >
        <ModalHeader toggle={() => setShowForm(false)}>
          {editId ? "Update" : "Add"} Charges / Discount
        </ModalHeader>
        <ModalBody>
          <Form className="oc-form" onSubmit={submitForm}>
            <Row>
              <Col lg="6" md="6" sm="12">
                <FormGroup>
                  <Label>
                    Narration Type <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    type="select"
                    value={formData.type}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, type: e.target.value }))
                    }
                  >
                    <option value="">-- Select --</option>
                    <option value="Charges">Charges</option>
                    <option value="Discount">Discount</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col lg="6" md="6" sm="12">
                <FormGroup>
                  <Label>
                    Mode <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    type="select"
                    value={formData.discounttype}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        discounttype: e.target.value,
                        // reset the other field when toggling
                        amount: e.target.value === "Amount" ? p.amount : "",
                        percentage:
                          e.target.value === "Percentage" ? p.percentage : "",
                      }))
                    }
                  >
                    <option value="">-- Select --</option>
                    <option value="Amount">Amount</option>
                    <option value="Percentage">Percentage</option>
                  </Input>
                </FormGroup>
              </Col>

              <Col lg="12" md="12" sm="12">
                <FormGroup>
                  <Label>
                    Title <span style={{ color: "red" }}>*</span>
                  </Label>
                  <Input
                    required
                    placeholder="Enter title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                </FormGroup>
              </Col>

              {formData.discounttype === "Amount" && (
                <Col lg="6" md="6" sm="12">
                  <FormGroup>
                    <Label>
                      Amount (₹) <span style={{ color: "red" }}>*</span>
                    </Label>
                    <Input
                      required
                      type="number"
                      placeholder="Enter amount"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, amount: e.target.value }))
                      }
                    />
                  </FormGroup>
                </Col>
              )}

              {formData.discounttype === "Percentage" && (
                <Col lg="6" md="6" sm="12">
                  <FormGroup>
                    <Label>
                      Percentage (%) <span style={{ color: "red" }}>*</span>
                    </Label>
                    <Input
                      required
                      type="number"
                      placeholder="Enter percentage"
                      value={formData.percentage}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          percentage: e.target.value,
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
              )}
            </Row>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 8,
              }}
            >
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : editId ? "Update" : "Save"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
